import { useCallback, useEffect, useRef } from 'react';

import { useOwner } from '@codes/owner';

import { fetchProfile, refreshAuthToken } from '../api';
import { ENV, PROJECT } from '../core';
import { useWebCoreStore } from '../stores';
import { classifyError } from '../utils';

import type { ErrorClassification } from '../utils';

const REFRESH_INTERVAL = 1000 * 60; // 1분
const MIN_REFRESH_GAP = 5000; // 5초 간격 제한

export const useTokenRefresh = (webCoreReady: boolean) => {
    const { isAuthenticated, setProfile, logout, initState } = useWebCoreStore();
    const { fetchAndSetOwner } = useOwner();

    const intervalRef = useRef<NodeJS.Timer | null>(null);
    const isRefreshingRef = useRef(false);
    const lastRefreshTime = useRef(0);
    const isInitializedRef = useRef(false);

    const refreshToken = useCallback(async (): Promise<boolean> => {
        const now = Date.now();
        const isDuplicated = now - lastRefreshTime.current < MIN_REFRESH_GAP || isRefreshingRef.current;
        if (isDuplicated) {
            return true;
        }

        isRefreshingRef.current = true;
        lastRefreshTime.current = now;

        try {
            await refreshAuthToken();
            return true;
        } catch (error) {
            console.error('❌ Token refresh failed:', error);
            const errorClassification: ErrorClassification = classifyError(error);
            if (errorClassification.shouldLogout) {
                console.log('🚪 Token completely expired or invalid - logging out...');
                await logout();
                return false;
            }
            console.log('⚠️ Temporary refresh failure, will retry later');
            return true;
        } finally {
            isRefreshingRef.current = false;
        }
    }, [logout]);

    const startInterval = useCallback(() => {
        if (intervalRef.current) {
            return;
        }

        console.log(`🚀 Starting token refresh interval: ${REFRESH_INTERVAL}ms`);
        intervalRef.current = setInterval(refreshToken, REFRESH_INTERVAL);
    }, [refreshToken]);

    const stopInterval = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            console.log('🛑 Stopped token refresh interval');
        }
    }, []);

    const loadProfile = useCallback(async () => {
        const profile = await fetchProfile();

        const isAdminProject = PROJECT.includes('admin');
        const isWebProject = PROJECT.includes('web');

        if (isAdminProject) {
            const isNotAdmin = !profile?.roles || !profile?.roles.includes('admin');
            if (isNotAdmin && ENV === 'prod') {
                alert('You are not authorized to access the admin page.');
                window.location.href = '/auth/logout';
                return;
            }

            setProfile(profile);
        }

        if (isWebProject) {
            await fetchAndSetOwner();
            setProfile(profile);
        }

        isInitializedRef.current = true;
    }, [setProfile, fetchAndSetOwner]);

    const is403Error = useCallback((error: any): boolean => {
        return error?.status === 403 || error?.response?.status === 403 || error?.message?.includes('403');
    }, []);

    const handleProfileFetch403Error = useCallback(async () => {
        console.log('🔄 Profile fetch got 403, refreshing token once more...');
        const refreshSuccess = await refreshToken();
        if (!refreshSuccess) {
            return;
        }

        try {
            await loadProfile();
            console.log('✅ Initialization succeeded after additional token refresh');
        } catch (retryError) {
            console.error('❌ Profile fetch failed even after token refresh:', retryError);
        }
    }, [refreshToken, loadProfile]);

    const initialize = useCallback(async () => {
        if (!isAuthenticated || !webCoreReady || isInitializedRef.current) {
            return;
        }
        console.log('🏁 Initializing: checking token validity...');

        try {
            if (initState !== 'refreshed') {
                console.log('🔄 Token not refreshed during init, attempting refresh...');
                const refreshSuccess = await refreshToken();
                if (!refreshSuccess) {
                    return;
                }
            } else {
                console.log('⏭️ Token already refreshed during webCore.init(), skipping refresh...');
            }

            await loadProfile();
        } catch (error) {
            console.error('❌ Profile fetch failed:', error);
            if (is403Error(error)) {
                await handleProfileFetch403Error();
            }
        }
    }, [isAuthenticated, refreshToken, webCoreReady, initState, loadProfile, is403Error, handleProfileFetch403Error]);

    useEffect(() => {
        if (isAuthenticated && webCoreReady) {
            initialize().then(() => {
                if (isInitializedRef.current) {
                    startInterval();
                }
            });
        } else {
            stopInterval();
            isInitializedRef.current = false;
        }

        return stopInterval;
    }, [isAuthenticated, initialize, startInterval, stopInterval, webCoreReady]);

    return {
        refreshToken,
        isRefreshing: isRefreshingRef.current,
        isInitialized: isInitializedRef.current,
    };
};
